/**
 * Click-to-toggle dropdown/popover state that closes on an outside click
 * or Escape — the behaviour native `<details>` lacks (it stays open until
 * you tap the summary again). Put `ref` on the wrapper that contains BOTH
 * the trigger and the popover; the outside-click check uses it.
 *
 * Usage:
 *   const menu = useDropdown<HTMLDivElement>()
 *   <div ref={menu.ref} className="relative">
 *     <button aria-expanded={menu.open} onClick={menu.toggle}>…</button>
 *     {menu.open && <div className="absolute …">…</div>}
 *   </div>
 */
import { useEffect, useRef, useState } from 'react'
import { usePresence, type PresenceStatus } from '@vollgas/shared/hooks'

// Popover enter/exit duration — matches --transition-fast (120ms).
const POPOVER_MS = 120

export interface Dropdown<T extends HTMLElement> {
  readonly ref: React.RefObject<T | null>
  readonly open: boolean
  readonly toggle: () => void
  readonly close: () => void
  /** Render the popover while true — stays mounted through its exit animation. */
  readonly mounted: boolean
  /** `'open'` plays the enter animation, `'closing'` the exit. */
  readonly status: PresenceStatus
}

export function useDropdown<T extends HTMLElement = HTMLDivElement>(): Dropdown<T> {
  const ref = useRef<T>(null)
  const [open, setOpen] = useState(false)
  const { mounted, status } = usePresence(open, POPOVER_MS)

  useEffect(() => {
    if (!open) return
    // `pointerdown` (not `click`) so the menu closes on press, before any
    // click handler elsewhere runs — matches native menu feel.
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return {
    ref,
    open,
    toggle: () => setOpen((o) => !o),
    close: () => setOpen(false),
    mounted,
    status,
  }
}
